import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Document, documentTable } from "models/schemas";
import { CreateDocumentFields } from "models/schemas";
import { FileMetadata } from "components/input-files/definitions";
import {
  RegisterDocumentRequest,
  RegisterDocumentResponse,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/create-audit-form/definitions";
import { DownloadUrlRequest, DeleteDocumentsRequest } from "./definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { deleteInfo } from "./table-config";
import { FileOperationsProps } from "./definitions";
import { mainApi } from "services/main";
import { DeleteIngestedDocumentsResponse } from "pages/gaia-ai/definitions";
import {
  DocumentCollection,
  VersionedDocument,
  createDocumentCollection,
} from "./version-utils";
import {
  uploadFileToS3,
  extractUniqueKey,
  getDocumentUrlFromPresigned,
  sleep,
} from "utils/document-utils";
import {
  uploadFileWithProgress,
  UploadProgress,
  CancelToken,
} from "utils/upload-progress-utils";

export const useFileOperations = (props: FileOperationsProps) => {
  const {
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    updateDocumentForMoveCall,
    lazyDownloadUrlCall,
    ingestDocuments,
    deleteIngestedDocuments,
    addNotification,
    teamName,
    currentUser,
    parentId,
    gaiaObjectId,
    category,
    displayMode,
    setDocs,
    setSelectedItems,
    setSingleDoc,
    setDeleteDocModalIsVisible,
    setDocToRename,
    setFileName,
  } = props;

  const dispatch = useDispatch();

  const getDocDetail = useCallback(
    (
      documentId: string,
      documentType: string,
      docs: documentTable[],
      flatDocs: Document[]
    ) => {
      if (displayMode === "flat") {
        return flatDocs.find((doc) => doc.documentId === documentId) || null;
      }
      return (
        docs
          .find((item) => item.documentId === documentType)
          ?.children.find((child) => child.documentId === documentId) || null
      );
    },
    [displayMode]
  );

  const getFileNames = useCallback(
    (singleDoc: Document | undefined, selectedItems: any[]) => {
      if (!singleDoc) {
        return selectedItems?.map((d: Document) => d.fileName).join(", ") || "";
      }
      return singleDoc?.fileName;
    },
    []
  );

  const downloadSingleDoc = useCallback(
    async (doc: Document) => {
      try {
        if (doc.s3Prefix) {
          let request: DownloadUrlRequest = {
            documentId: doc.documentId,
            preview: false,
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (e) {
        addNotification({
          type: "error",
          header: `File download failed.`,
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification]
  );

  const downloadDocs = useCallback(
    async (selectedItems: any[]) => {
      for (const doc of selectedItems) {
        await downloadSingleDoc(doc);
        await sleep(500);
      }
      setSelectedItems([]);
    },
    [downloadSingleDoc, setSelectedItems]
  );

  const handleDocDownload = useCallback(
    async (
      docId: string,
      docType: string,
      docs: documentTable[],
      flatDocs: Document[]
    ) => {
      await downloadSingleDoc(
        getDocDetail(docId, docType, docs, flatDocs) as Document
      );
    },
    [downloadSingleDoc, getDocDetail]
  );

  const changeDocFileName = useCallback(
    async (editDoc: Document, fileName: string) => {
      try {
        const documentCollection = props.documentCollection;
        const allVersions = documentCollection
          ? documentCollection.getVersionsForDocument(editDoc)
          : [editDoc];

        await updateDocCall({
          documentIds: allVersions.map((v) => v.documentId),
          fileName: fileName,
        }).unwrap();

        if (props.useAtlasEndpoints && props.setAtlasDocuments) {
          props.setAtlasDocuments((prev) =>
            prev.map((doc) =>
              doc.documentId === editDoc.documentId ? { ...doc, fileName } : doc
            )
          );
        }

        setDocToRename(undefined);
        setFileName("");

        addNotification({
          type: "success",
          header:
            editDoc.extentionType === "Link"
              ? "Link updated."
              : "File updated.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header:
            editDoc.extentionType === "Link"
              ? "Link update failed."
              : "File update failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      updateDocCall,
      props.useAtlasEndpoints,
      props.setAtlasDocuments,
      props.setDocumentCollection,
      props.documentCollection,
      setDocToRename,
      setFileName,
      addNotification,
    ]
  );

  const onCreateDoc = useCallback(
    async (
      doc: CreateDocumentFields,
      file: File,
      link: string,
      fileName: string,
      callback: () => void
    ) => {
      let uploadUrlResponse: UploadUrlResponse | null = null;
      try {
        if (file) {
          let request: UploadUrlRequest = {
            prefix: category + "/" + parentId,
            fileName: file.name,
            teamName: teamName,
            securityLevel: "Normal",
          };
          uploadUrlResponse = await uploadUrlCall(request).unwrap();

          if (uploadUrlResponse) {
            await uploadFileToS3(file, uploadUrlResponse);
          }
        }

        const s3Prefix = uploadUrlResponse
          ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${file.name}`
          : "";
        const uploadDocRequest: RegisterDocumentRequest = {
          parentId: parentId,
          teamName: teamName,
          fileName: fileName || getFileNameWithoutExtension(file.name),
          documentType: doc.documentType,
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          externalUrl: link,
          extentionType: file ? getFileExtension(file.name) : "Link",
          ...(file && { size: getFileSize(file.size) }),
        };
        if (gaiaObjectId.startsWith("audit")) {
          uploadDocRequest.auditId = gaiaObjectId;
        } else {
          uploadDocRequest.gaiaObjectId = gaiaObjectId;
        }
        const uploadDocResponse: RegisterDocumentResponse =
          await registerDocCall(uploadDocRequest).unwrap();
        callback();

        const newDoc: Document = {
          ...uploadDocRequest,
          documentId: uploadDocResponse.documentId,
          createdAt: (Number(Date.now().toString()) / 1000).toString(),
          createdBy: currentUser,
          updatedAt: (Number(Date.now().toString()) / 1000).toString(),
          updatedBy: currentUser,
          deleted: false,
        };

        if (displayMode === "grouped") {
          setDocs((prevDocs: documentTable[]) => {
            const updatedDocs = prevDocs.map((item) => {
              if (item.documentId === newDoc.documentType) {
                return {
                  ...item,
                  children: [...item.children, newDoc],
                };
              }
              return item;
            });
            return updatedDocs;
          });
        }

        addNotification({
          type: "success",
          header: file ? "File uploaded." : "Link added.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: file ? "File upload failed." : "Link add failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      parentId,
      gaiaObjectId,
      currentUser,
      displayMode,
      setDocs,
    ]
  );

  const onMultipleFileUpload = useCallback(
    async (
      files: FileMetadata[],
      callback: () => void,
      progressCallback?: (progress: UploadProgress) => void,
      cancelToken?: CancelToken
    ) => {
      let successCount = 0;
      let failedCount = 0;
      const newDocs: Document[] = [];

      try {
        for (let i = 0; i < files.length; i++) {
          // Check for cancellation before processing each file
          if (cancelToken?.cancelled) {
            break;
          }

          const fileMetadata = files[i];

          try {
            // Update progress: starting new file
            if (progressCallback) {
              progressCallback({
                isUploading: true,
                currentFileIndex: i + 1,
                totalFiles: files.length,
                currentFileName: fileMetadata.file.name,
                currentFileProgress: 0,
                successCount,
                failedCount,
                cancelRequested: cancelToken?.cancelled || false,
              });
            }

            // Get upload URL
            let request: UploadUrlRequest = {
              prefix: category + "/" + parentId,
              fileName: fileMetadata.file.name,
              teamName: teamName,
              securityLevel: "Normal",
            };
            const uploadUrlResponse = await uploadUrlCall(request).unwrap();

            // Upload file to S3 with progress tracking
            if (uploadUrlResponse) {
              await uploadFileWithProgress(
                uploadUrlResponse.url,
                fileMetadata.file,
                uploadUrlResponse.bucketOwner,
                (percentage) => {
                  if (progressCallback) {
                    progressCallback({
                      isUploading: true,
                      currentFileIndex: i + 1,
                      totalFiles: files.length,
                      currentFileName: fileMetadata.file.name,
                      currentFileProgress: percentage,
                      successCount,
                      failedCount,
                      cancelRequested: cancelToken?.cancelled || false,
                    });
                  }
                },
                cancelToken
              );
            }

            // Register document
            const s3Prefix = uploadUrlResponse
              ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${fileMetadata.file.name}`
              : "";
            const uploadDocRequest: RegisterDocumentRequest = {
              parentId: parentId,
              teamName: teamName,
              fileName: fileMetadata.fileName,
              documentType: fileMetadata.documentType,
              securityLevel: "Normal",
              s3Prefix: s3Prefix,
              externalUrl: "",
              extentionType: getFileExtension(fileMetadata.file.name),
              size: getFileSize(fileMetadata.file.size),
            };

            // Add either auditId or gaiaObjectId based on the type
            if (gaiaObjectId.startsWith("audit")) {
              uploadDocRequest.auditId = gaiaObjectId;
            } else {
              uploadDocRequest.gaiaObjectId = gaiaObjectId;
            }

            const uploadDocResponse: RegisterDocumentResponse =
              await registerDocCall(uploadDocRequest).unwrap();

            const newDoc = {
              ...uploadDocRequest,
              documentId: uploadDocResponse.documentId,
              createdAt: (Number(Date.now().toString()) / 1000).toString(),
              createdBy: currentUser,
              updatedAt: (Number(Date.now().toString()) / 1000).toString(),
              updatedBy: currentUser,
              deleted: false,
            };

            newDocs.push(newDoc);
            successCount++;
          } catch (fileError) {
            console.error(
              `Failed to upload ${fileMetadata.file.name}:`,
              fileError
            );
            failedCount++;
            // Continue with next file
          }
        }

        if (newDocs.length > 0) {
          if (props.useAtlasEndpoints && props.setAtlasDocuments) {
            props.setAtlasDocuments((prevDocs) => [...prevDocs, ...newDocs]);
          } else if (props.setDocumentCollection) {
            props.setDocumentCollection((prev) => {
              let collection = prev || createDocumentCollection([]);
              newDocs.forEach((doc) => {
                collection = collection.addDocument(doc);
              });
              return collection;
            });
          }
        }

        callback();

        // Show notification with success/failure counts
        if (cancelToken?.cancelled) {
          addNotification({
            type: "info",
            header: `Upload cancelled. ${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded successfully${
              failedCount > 0 ? `, ${failedCount} failed` : ""
            }.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (failedCount === 0) {
          addNotification({
            type: "success",
            header: `${successCount} file${
              successCount > 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (successCount === 0) {
          addNotification({
            type: "error",
            header: `All ${files.length} file${
              files.length > 1 ? "s" : ""
            } failed to upload.`,
            dismissible: true,
            timeout: 10000,
          });
        } else {
          addNotification({
            type: "warning",
            header: `${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded successfully. ${failedCount} failed.`,
            dismissible: true,
            timeout: 10000,
          });
        }
      } catch (e) {
        callback();
        addNotification({
          type: "error",
          header: `Upload failed.`,
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      gaiaObjectId,
      currentUser,
      parentId,
    ]
  );

  const deleteDoc = useCallback(
    async (doc: Document) => {
      if (deleteIngestedDocuments && doc.documentId.match(/.+\/kb\/.+/)) {
        // For Atlas documents, use the Atlas KB delete endpoint
        const atlasRequest = {
          teamName: teamName,
          documents: [doc.documentId],
        };
        const resp: DeleteIngestedDocumentsResponse =
          await deleteIngestedDocuments(atlasRequest).unwrap();
        if (resp.failedDocuments.length > 0) {
          resp.failedDocuments.forEach((d) => {
            console.error(
              `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`
            );
          });
          throw new Error("Failed to delete ingested document");
        }
      } else {
        // For regular documents, use the standard delete endpoint
        let request: DeleteDocumentsRequest = {
          documentIds: [doc.documentId],
        };
        await deleteDocCall(request).unwrap();
      }

      if (displayMode === "grouped") {
        setDocs((prevDocs: documentTable[]) => {
          const updatedDocs = prevDocs.map((item) => {
            if (item.documentId === doc.documentType) {
              return {
                ...item,
                children: item.children.filter(
                  (childDoc) => childDoc.documentId !== doc.documentId
                ),
              };
            }
            return item;
          });
          return updatedDocs;
        });
      }
    },
    [deleteDocCall, displayMode, setDocs]
  );

  const deleteDocs = useCallback(
    async (docs: Document[]) => {
      const ATLAS_BATCH_SIZE = 50;

      try {
        // Check if these are Atlas documents (all docs in a call are the same type)
        const isAtlasDocs =
          deleteIngestedDocuments && docs[0]?.documentId.match(/.+\/kb\/.+/);

        if (isAtlasDocs) {
          // Delete Atlas documents in batches of up to 50
          for (let i = 0; i < docs.length; i += ATLAS_BATCH_SIZE) {
            const batch = docs.slice(i, i + ATLAS_BATCH_SIZE);
            const atlasRequest = {
              teamName: teamName,
              documents: batch.map((doc) => doc.documentId),
            };
            const resp: DeleteIngestedDocumentsResponse =
              await deleteIngestedDocuments(atlasRequest).unwrap();

            if (resp.failedDocuments.length > 0) {
              resp.failedDocuments.forEach((d) => {
                console.error(
                  `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`
                );
              });
              throw new Error("Failed to delete some ingested documents");
            }
          }
        } else {
          // Delete regular documents using batch API
          // Cache invalidation in deleteDocCall triggers refetch automatically
          const request: DeleteDocumentsRequest = {
            documentIds: docs.map((doc) => doc.documentId),
          };
          await deleteDocCall(request).unwrap();
        }

        addNotification({
          type: "success",
          header: deleteInfo(docs, " succeeded"),
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: deleteInfo(docs, " failed"),
          dismissible: true,
          timeout: 10000,
        });
      } finally {
        setSelectedItems([]);
        setSingleDoc(undefined);
        setDeleteDocModalIsVisible(false);
      }
    },
    [
      deleteIngestedDocuments,
      deleteDocCall,
      teamName,
      addNotification,
      setSelectedItems,
      setSingleDoc,
      setDeleteDocModalIsVisible,
    ]
  );

  // Atlas-specific upload function
  const onCreateDocForAtlas = useCallback(
    async (
      files: File[],
      teamName: string,
      callback: () => void,
      replaceExisting: boolean = false,
      existingDocs: Document[] = [],
      progressCallback?: (progress: UploadProgress) => void,
      cancelToken?: CancelToken
    ) => {
      if (!ingestDocuments) {
        addNotification({
          type: "error",
          header: "Atlas document ingestion not available.",
          dismissible: true,
          timeout: 10000,
        });
        callback();
        return;
      }
      let successCount = 0;
      let failedCount = 0;

      try {
        const items: Array<{
          documentUrl: string;
          size: string;
          replaceExisting: boolean;
        }> = [];

        // Create a Map for O(1) lookup of existing documents by filename
        const existingDocsMap = replaceExisting
          ? new Map(
              existingDocs.map((doc) => [
                doc.fileName + (doc.extentionType || ""),
                doc,
              ])
            )
          : null;

        // Upload each file to S3 and prepare for Atlas ingestion
        for (let i = 0; i < files.length; i++) {
          // Check for cancellation before processing each file
          if (cancelToken?.cancelled) {
            break;
          }

          const file = files[i];

          try {
            // Update progress: starting new file
            if (progressCallback) {
              progressCallback({
                isUploading: true,
                currentFileIndex: i + 1,
                totalFiles: files.length,
                currentFileName: file.name,
                currentFileProgress: 0,
                successCount,
                failedCount,
                cancelRequested: cancelToken?.cancelled || false,
              });
            }

            // Check if this file is a duplicate and we're in replace mode
            const existingDoc = existingDocsMap
              ? existingDocsMap.get(file.name) || null
              : null;

            // Extract uniqueKey from existing document if this is a replacement
            const existingUniqueKey = existingDoc
              ? extractUniqueKey(existingDoc.documentId)
              : null;

            // Call UploadPresignUrl with optional uniqueKey parameter
            const request: UploadUrlRequest & { uniqueKey?: string } = {
              prefix: "kb",
              fileName: file.name,
              teamName: teamName,
              securityLevel: "Normal",
              ...(existingUniqueKey && { uniqueKey: existingUniqueKey }),
            };

            const uploadUrlResponse = await uploadUrlCall(request).unwrap();

            if (!uploadUrlResponse) {
              throw new Error("Failed to get upload URL");
            }

            // Upload file to S3 with progress tracking
            await uploadFileWithProgress(
              uploadUrlResponse.url,
              file,
              uploadUrlResponse.bucketOwner,
              (percentage) => {
                if (progressCallback) {
                  progressCallback({
                    isUploading: true,
                    currentFileIndex: i + 1,
                    totalFiles: files.length,
                    currentFileName: file.name,
                    currentFileProgress: percentage,
                    successCount,
                    failedCount,
                    cancelRequested: cancelToken?.cancelled || false,
                  });
                }
              },
              cancelToken
            );

            // Extract document URL from presigned URL
            const documentUrl = getDocumentUrlFromPresigned(
              uploadUrlResponse.url
            );

            // Add item with documentUrl and replaceExisting flag
            items.push({
              documentUrl: documentUrl,
              size: getFileSize(file.size),
              replaceExisting: !!existingUniqueKey,
            });

            successCount++;
          } catch (fileError) {
            console.error(`Failed to upload ${file.name}:`, fileError);
            failedCount++;
            // Continue with next file
          }
        }

        // Call IngestDocuments API only if there are successful uploads
        if (items.length > 0) {
          const request = {
            teamName: teamName,
            documentIngestions: {
              items: items,
            },
          };

          await ingestDocuments(request).unwrap();
        }

        callback();

        // Show notification with success/failure counts
        if (cancelToken?.cancelled) {
          addNotification({
            type: "info",
            header: `Upload cancelled. ${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded to Atlas${
              failedCount > 0 ? `, ${failedCount} failed` : ""
            }.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (failedCount === 0) {
          addNotification({
            type: "success",
            header: `${successCount} file${
              successCount > 1 ? "s" : ""
            } uploaded to Atlas successfully.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (successCount === 0) {
          addNotification({
            type: "error",
            header: `All ${files.length} file${
              files.length > 1 ? "s" : ""
            } failed to upload to Atlas.`,
            dismissible: true,
            timeout: 10000,
          });
        } else {
          addNotification({
            type: "warning",
            header: `${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded to Atlas. ${failedCount} failed.`,
            dismissible: true,
            timeout: 10000,
          });
        }
      } catch (error) {
        callback();
        const is400Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 400;
        addNotification({
          type: "error",
          header: is400Error
            ? "Atlas document upload failed - file type is not supported."
            : "Atlas document upload failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [uploadUrlCall, ingestDocuments, addNotification]
  );

  // Team-level file upload function
  const onCreateTeamFiles = useCallback(
    async (
      files: File[],
      teamName: string,
      callback: () => void,
      replaceExisting?: boolean,
      existingDocs?: Document[],
      progressCallback?: (progress: UploadProgress) => void,
      cancelToken?: CancelToken
    ) => {
      let successCount = 0;
      let failedCount = 0;

      try {
        for (let i = 0; i < files.length; i++) {
          // Check for cancellation before processing each file
          if (cancelToken?.cancelled) {
            break;
          }

          const file = files[i];

          try {
            // Update progress: starting new file
            if (progressCallback) {
              progressCallback({
                isUploading: true,
                currentFileIndex: i + 1,
                totalFiles: files.length,
                currentFileName: file.name,
                currentFileProgress: 0,
                successCount,
                failedCount,
                cancelRequested: cancelToken?.cancelled || false,
              });
            }

            // Get upload URL
            let request: UploadUrlRequest = {
              prefix: category + "/" + parentId,
              fileName: file.name,
              teamName: teamName,
              securityLevel: "Normal",
            };
            const uploadUrlResponse = await uploadUrlCall(request).unwrap();

            // Upload file to S3 with progress tracking
            if (uploadUrlResponse) {
              await uploadFileWithProgress(
                uploadUrlResponse.url,
                file,
                uploadUrlResponse.bucketOwner,
                (percentage) => {
                  if (progressCallback) {
                    progressCallback({
                      isUploading: true,
                      currentFileIndex: i + 1,
                      totalFiles: files.length,
                      currentFileName: file.name,
                      currentFileProgress: percentage,
                      successCount,
                      failedCount,
                      cancelRequested: cancelToken?.cancelled || false,
                    });
                  }
                },
                cancelToken
              );
            }

            // Register document
            const s3Prefix = uploadUrlResponse
              ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${file.name}`
              : "";
            const uploadDocRequest: RegisterDocumentRequest = {
              parentId: parentId,
              teamName: teamName,
              fileName: getFileNameWithoutExtension(file.name),
              documentType: "Team",
              securityLevel: "Normal",
              s3Prefix: s3Prefix,
              externalUrl: "",
              extentionType: getFileExtension(file.name),
              size: getFileSize(file.size),
            };

            const uploadDocResponse: RegisterDocumentResponse =
              await registerDocCall(uploadDocRequest).unwrap();

            successCount++;
          } catch (fileError) {
            console.error(`Failed to upload ${file.name}:`, fileError);
            failedCount++;
            // Continue with next file
          }
        }

        callback();

        // Show notification with success/failure counts
        if (cancelToken?.cancelled) {
          addNotification({
            type: "info",
            header: `Upload cancelled. ${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded successfully${
              failedCount > 0 ? `, ${failedCount} failed` : ""
            }.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (failedCount === 0) {
          addNotification({
            type: "success",
            header: `${successCount} file${
              successCount > 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (successCount === 0) {
          addNotification({
            type: "error",
            header: `All ${files.length} file${
              files.length > 1 ? "s" : ""
            } failed to upload.`,
            dismissible: true,
            timeout: 10000,
          });
        } else {
          addNotification({
            type: "warning",
            header: `${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded successfully. ${failedCount} failed.`,
            dismissible: true,
            timeout: 10000,
          });
        }
      } catch (e) {
        callback();
        addNotification({
          type: "error",
          header: `Upload failed.`,
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      parentId,
      currentUser,
    ]
  );

  const onCreateFolder = useCallback(
    async (folderName: string) => {
      try {
        const registerFolderRequest: RegisterDocumentRequest = {
          parentId: parentId,
          teamName: teamName,
          fileName: folderName,
          documentType: "Team",
          securityLevel: "Normal",
          s3Prefix: "",
          externalUrl: "",
          extentionType: "Folder",
        };
        await registerDocCall(registerFolderRequest).unwrap();

        addNotification({
          type: "success",
          header: "Folder created successfully.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: "Folder creation failed.",
          dismissible: true,
          timeout: 10000,
        });
        throw e;
      }
    },
    [
      registerDocCall,
      teamName,
      parentId,
      addNotification,
      displayMode,
      setDocs,
      currentUser,
    ]
  );

  const moveFiles = useCallback(
    async (
      files: Document[],
      targetParentId: string,
      documentCollection?: DocumentCollection
    ) => {
      try {
        const allFilesToMove: Document[] = [...files];

        // Add older versions using DocumentCollection
        if (documentCollection) {
          files.forEach((file) => {
            const allVersions = documentCollection.getVersionsForDocument(file);
            const olderVersions = allVersions.filter(
              (v: VersionedDocument) => !v.isLatest
            );
            allFilesToMove.push(...olderVersions);
          });
        }

        await updateDocumentForMoveCall({
          documentIds: allFilesToMove.map((file) => file.documentId),
          parentId: targetParentId,
        }).unwrap();

        setSelectedItems([]);

        addNotification({
          type: "success",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } moved successfully.`,
          dismissible: true,
          timeout: 10000,
        });

        // Calculate delay based on number of files moved
        const baseDelay = 2000;
        const perFileDelay = 750;
        // We should delay max 5s for UX
        const calculatedDelay = Math.min(
          baseDelay + (allFilesToMove.length - 1) * perFileDelay,
          5000
        );
        // Manually invalidate cache
        setTimeout(() => {
          dispatch(mainApi.util.invalidateTags(["Documents"]));
        }, calculatedDelay);
      } catch (e) {
        addNotification({
          type: "error",
          header: "Move files failed.",
          dismissible: true,
          timeout: 10000,
        });
        throw e;
      }
    },
    [updateDocumentForMoveCall, addNotification, dispatch, setSelectedItems]
  );

  return {
    getDocDetail,
    getFileNames,
    downloadSingleDoc,
    downloadDocs,
    handleDocDownload,
    changeDocFileName,
    onCreateDoc,
    onMultipleFileUpload,
    onCreateDocForAtlas,
    onCreateTeamFiles,
    onCreateFolder,
    deleteDoc,
    deleteDocs,
    moveFiles,
  };
};
